import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil
} from '@cannect/assets/img'
import { useProductClassId } from '@cannect/services/resources/classes'
import { useAuth } from '@cannect/hooks/AuthContext'
import { useSuggestedProducts } from '@cannect/hooks/useSuggestedProducts'
import Button from '@cannect/new-components/Button'
import Modal from '@cannect/new-components/Modal'
import { ShoppingCart } from 'lucide-react'

interface Product {
  id: number
  name?: string
  translated_name?: string
  price: number
  promotional_price?: number
  real_price?: number
  original_price?: number
  media?: Array<{ path: string; format: string }>
  brand_id?: number
  type?: string
  class_id?: number
  supplier_id?: number
  stock?: number
  currency?: string
  promotion?: Array<{
    regular_price?: any
    promotional_price?: any
    discount_value?: any
    discount_type?: any
  }>
}

interface SuggestedProductsProps {
  onAddToCart: (product: Product) => void
}

export function SuggestedProducts({ onAddToCart }: SuggestedProductsProps) {
  const { getClassIdByName } = useProductClassId()
  const hygieneBeautyId = getClassIdByName('Higiene e Beleza')

  const {
    products,
    isLoading,
    error,
    addingProduct,
    handleAddToCart: handleAdd
  } = useSuggestedProducts({
    limit: 3,
    filterByCartItems: true,
    classId: hygieneBeautyId
  })

  const { role } = useAuth()

  if (isLoading || error || products.length === 0) {
    return null
  }

  return (
    <div className="mt-4 sm:mt-6">
      <h3 className="mb-3 text-base font-semibold text-gray-800 sm:text-lg">Aproveite Também</h3>
      <div>
        {products.map((product: Product, index: number) => {
          const getProductImage = (product: any) => {
            const isNationalProduct = product?.supplier?.id === 18
            const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
            const imgMedia = product?.media?.find((m: any) => m?.format === 'IMG')?.path
            const shapeNameFormatted = product?.shape_id
            const isSupplementProduct = product?.class_id === 6
            const isPillProduct = product?.origin === 'pill'

            const isPatientUserType = role === 1

            let imageSource = ''

            if (isPillProduct) {
              return imgMedia
            }

            switch (shapeNameFormatted) {
              case 1:
                imageSource =
                  isPatientUserType && !isSupplementProduct
                    ? cannectDefaultProductOil
                    : imgMedia || cannectDefaultProductOil
                break
              case 2:
                imageSource =
                  isPatientUserType && !isSupplementProduct
                    ? cannectDefaultProductCapsule
                    : imgMedia || cannectDefaultProductCapsule
                break
              case 4:
                imageSource =
                  isPatientUserType && !isSupplementProduct
                    ? cannectDefaultProductCream
                    : imgMedia || cannectDefaultProductCream
                break
              case 6:
                imageSource =
                  isPatientUserType && !isSupplementProduct
                    ? cannectDefaultProductGummy
                    : imgMedia || cannectDefaultProductGummy
                break
              default:
                imageSource = isPatientUserType && !isSupplementProduct ? defaultImage : imgMedia || defaultImage
                break
            }

            return imageSource
          }
          return (
            <div key={product.id}>
              <div className="flex items-start gap-3 overflow-hidden rounded-lg border border-gray-200 p-3 sm:gap-4 sm:p-0">
                <img
                  src={getProductImage(product)}
                  alt={product.translated_name}
                  className="h-20 w-20 flex-shrink-0 rounded-lg border border-gray-200 object-cover sm:h-32 sm:w-32"
                />

                <div className="flex flex-1 flex-col gap-2 sm:px-4 sm:py-2">
                  <p className="line-clamp-2 text-xs font-semibold text-gray-800 sm:text-sm">
                    {product.translated_name}
                  </p>

                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-1">
                      {product.promotion && product.promotion.length > 0 && (
                        <>
                          <span className="text-xs text-gray-400 line-through">
                            R$ {parseFloat(product?.promotion[0]?.regular_price)}
                          </span>
                          {product.promotion[0]?.discount_type === 'percent' ? (
                            <span className="text-[10px] italic text-primary-500 sm:text-[12px]">
                              -{product?.promotion[0]?.discount_value || 0}%
                            </span>
                          ) : (
                            <span className="text-[10px] italic text-primary-500 sm:text-[12px]">
                              - R$ {product.promotion[0]?.discount_value}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-gray-900 sm:text-base">
                      R${' '}
                      {Number(product.promotional_price || product.price)
                        ?.toFixed(2)
                        .replace('.', ',')}
                    </span>
                  </div>

                  <button
                    onClick={() => handleAdd(product, onAddToCart)}
                    disabled={addingProduct === product.id}
                    className="flex h-8 w-full items-center justify-center gap-1 rounded-sm bg-white px-2 py-1 text-xs font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:h-6 sm:w-fit sm:px-8"
                    style={{
                      border: '1px solid #E3E3E3',
                      borderColor: '#E3E3E3'
                    }}>
                    {addingProduct === product.id ? (
                      <>
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
                        <span className="hidden sm:inline">Adicionando...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-3 w-3 text-sm font-semibold text-gray-700" />
                        <span className="hidden sm:inline">Adicionar ao carrinho</span>
                        <span className="sm:hidden">Adicionar</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              {index < products.length - 1 && (
                <hr className="my-3 border-0 border-t border-solid border-[#E3E3E3] sm:my-4" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
